/**
 * spaceTabSelection — pure helpers for PDF-6.8 layout-tab multi-select +
 * PUBLISH sheet assembly (AutoCAD: shift/ctrl-click layout tabs, then
 * "Publish Selected Layouts").
 *
 * Kept out of the React component (`SpaceTabsBar`) so the selection algebra
 * (contiguous range select, ctrl-toggle) and the ordered `PublishSheet[]`
 * builder are unit-testable without a DOM. The component owns the `Set<string>`
 * + anchor state and calls these for each click.
 */

import type {
  DrawingDocument,
  LayoutPlotSettings,
} from "@dxf-ai-editor/drawing-model";

import type { PublishSheet } from "../print/pdf/publishLayouts";

/**
 * Toggle one layout id in the selection set (Ctrl/Cmd-click). Returns a NEW set
 * (never mutates the input) so React state updates stay referentially honest.
 */
export function toggleSelection(
  current: ReadonlySet<string>,
  id: string,
): Set<string> {
  const next = new Set(current);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

/**
 * Contiguous range select (Shift-click) between `anchorId` and `targetId` over
 * the VISIBLE ordered layout id list (`orderedIds` is the same ordered list the
 * tab strip renders — already filtered to layouts, in tab order).
 *
 * Returns the inclusive slice between the two ids, in tab order (independent of
 * which was clicked first). If either id is absent from `orderedIds` — or there
 * is no anchor — falls back to a single-element selection of the target
 * (mirrors AutoCAD: a shift-click with no prior anchor behaves like a plain
 * pick of that one tab).
 */
export function selectRange(
  orderedIds: readonly string[],
  anchorId: string | null,
  targetId: string,
): string[] {
  const targetIdx = orderedIds.indexOf(targetId);
  if (targetIdx < 0) return [];
  if (anchorId === null) return [targetId];
  const anchorIdx = orderedIds.indexOf(anchorId);
  if (anchorIdx < 0) return [targetId];
  const lo = Math.min(anchorIdx, targetIdx);
  const hi = Math.max(anchorIdx, targetIdx);
  return orderedIds.slice(lo, hi + 1);
}

/**
 * Resolve the ORDERED set of layout ids to publish from the current selection
 * and the right-clicked tab. AutoCAD parity:
 *   - if the right-clicked tab IS in the selection → publish the whole
 *     selection (in tab order);
 *   - if the right-clicked tab is NOT in the selection → publish just that one
 *     tab (fall back to a single-element selection).
 *
 * The result follows `orderedIds` (visible tab order), NOT Set insertion order,
 * so the multi-page PDF pages come out in the order the user sees the tabs.
 */
export function resolvePublishOrder(
  orderedIds: readonly string[],
  selected: ReadonlySet<string>,
  contextSpaceId: string,
): string[] {
  if (selected.has(contextSpaceId)) {
    return orderedIds.filter((id) => selected.has(id));
  }
  // Right-clicked tab not in the selection — publish just that one.
  return orderedIds.includes(contextSpaceId) ? [contextSpaceId] : [];
}

/** Look up a space's display name (for the sheet title / bookmark). */
function spaceName(doc: DrawingDocument, spaceId: string): string {
  return doc.spaces.find((s) => s.id === spaceId)?.name ?? spaceId;
}

/**
 * Build the ordered `PublishSheet[]` for {@link publishLayouts} from a list of
 * layout space ids (already in tab order). Each sheet resolves its OWN
 * persisted `plotSettings` from the model via `resolveSettings` (the source of
 * truth — never fabricated) and carries the layout name as its bookmark title.
 *
 * `resolveSettings` is injected (rather than importing `resolvePlotSettings`
 * directly) so this builder is unit-testable with a stub and stays decoupled
 * from the exporter module graph.
 */
export function buildPublishSheets(
  doc: DrawingDocument,
  orderedSpaceIds: readonly string[],
  resolveSettings: (doc: DrawingDocument, spaceId: string) => LayoutPlotSettings,
): PublishSheet[] {
  return orderedSpaceIds.map((spaceId) => ({
    spaceId,
    settings: resolveSettings(doc, spaceId),
    title: spaceName(doc, spaceId),
  }));
}
